import { store } from '../store';
import { setCurrentSession } from '../store/slices/coworkSlice';
import { setActiveSkillIds } from '../store/slices/skillSlice';
import type { CoworkSession } from '../types/cowork';
import { authService } from './auth';
import { scenarioService, type MyScenario } from './scenario';
import type { ScenarioSkillResolution } from './scenarioSkills';

/** 当前对话场景只作用于后续输入；固定 Agent、快速模式和桌面设备不能借用 Web 技能。 */
export function canApplyScenarioToConversation(session: CoworkSession | null): boolean {
  return Boolean(
    session &&
    session.origin === 'web' &&
    session.status !== 'running' &&
    session.promptMode !== 'quick' &&
    !session.agentSnapshot
  );
}

/** 异步准备固定当时的身份、平台和会话，不能在确认后跟随用户切换而改变目标。 */
export interface ScenarioConversationTarget {
  identity: string;
  baseUrl: string;
  sessionId: string;
}

/** 捕获可应用的当前目标；不修改会话、消息、草稿或服务端数据。 */
export function captureScenarioConversationTarget(): ScenarioConversationTarget | null {
  const identity = authService.getSessionIdentity();
  const session = store.getState().cowork.currentSession;
  return identity && canApplyScenarioToConversation(session)
    ? { identity, baseUrl: authService.getBaseUrl(), sessionId: session!.id }
    : null;
}

/** 准备前后复核身份和目标是否仍可修改；旧弹窗不能修改另一个会话。 */
export function isCurrentScenarioConversationTarget(target: ScenarioConversationTarget): boolean {
  const state = store.getState().cowork;
  return (
    authService.getSessionIdentity() === target.identity &&
    authService.getBaseUrl() === target.baseUrl &&
    state.currentSessionId === target.sessionId &&
    state.currentSession?.id === target.sessionId &&
    canApplyScenarioToConversation(state.currentSession)
  );
}

/** 提交已核验的场景上下文，保留现有消息与草稿；和 BoMate 一样只在后续发送时应用提示词。 */
export function applyScenarioToConversation(
  target: ScenarioConversationTarget,
  scenario: MyScenario,
  resolution: ScenarioSkillResolution
): boolean {
  if (!isCurrentScenarioConversationTarget(target)) return false;
  const session = store.getState().cowork.currentSession!;
  const activeSkillIds = resolution.ready.map((skill) => skill.id);
  scenarioService.saveSessionScenario(target.sessionId, scenario);
  scenarioService.markScenarioUsed(scenario.id);
  // 显式替换会话技能快照，避免新场景无就绪技能时续聊回退到旧场景技能。
  store.dispatch(setCurrentSession({ ...session, activeSkillIds }));
  store.dispatch(setActiveSkillIds(activeSkillIds));
  return true;
}
